<template>
  <div
    class="pa-4 px-md-6"
    :class="{ 'portal-grupo-familiar--dark': portalDarkModeActivo }"
  >
    <portal-page-header
      :title="portalScreenTitle || 'Grupo familiar'"
      :subtitle="portalScreenSubtitle"
    >
      <template #actions>
        <v-btn
          color="primary"
          class="text-uppercase"
          :prepend-icon="addMemberIcon"
          :disabled="loading || dialogAgregar"
          @click="abrirAgregar"
        >
          Agregar familiar
        </v-btn>
      </template>
    </portal-page-header>

    <v-alert
      type="info"
      color="secondary"
      rounded="lg"
      class="mb-4"
      variant="tonal"
      density="comfortable"
    >
      <template #prepend>
        <v-icon :icon="infoAlertIcon" color="secondary" />
      </template>
      <div>
        Puede solicitar vínculos con otras fichas indicando documento, fecha de nacimiento y parentesco.
        Las altas quedan <strong>pendientes de confirmación</strong> en la institución.
      </div>
    </v-alert>

    <v-card class="mx-auto" rounded="lg" elevation="1">
      <v-card-text class="py-3">
        <v-tabs
          v-model="mobileGrupoTab"
          class="d-md-none mb-2"
          color="primary"
          grow
          density="comfortable"
        >
          <v-tab :value="0" class="text-body-2 font-weight-medium text-uppercase">
            Integrantes
          </v-tab>
          <v-tab :value="1" class="text-body-2 font-weight-medium text-uppercase">
            Gráfico
          </v-tab>
        </v-tabs>

        <v-row dense align="stretch">
          <v-col
            v-if="listaColumnaVisible"
            cols="12"
            md="5"
            class="portal-grupo-familiar__lista-col d-flex flex-column"
          >
            <div v-if="loading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <template v-else>
              <div
                v-if="!vinculos.length"
                class="portal-grupo-familiar__empty d-flex flex-column align-center justify-center text-center flex-grow-1 px-2"
              >
                <v-icon :icon="emptyFamilyIcon" size="72" color="grey-lighten-1" aria-hidden="true" />
                <div class="text-body-1 text-medium-emphasis mt-4">
                  No hay familiares cargados
                </div>
              </div>
              <div v-else class="portal-grupo-familiar__lista-scroll flex-grow-1">
                <div v-for="grupo in gruposOrdenados" :key="grupo.key" class="mb-4">
                  <div class="portal-grupo-familiar__grupo-header text-caption font-weight-bold mb-2">
                    {{ grupo.titulo }}
                  </div>
                  <v-card
                    v-for="p in grupo.items"
                    :key="p.fichaParentescoId"
                    variant="outlined"
                    class="portal-grupo-familiar__familiar-card mb-2"
                  >
                    <div class="portal-grupo-familiar__familiar-card-main pa-2">
                      <div class="d-flex align-start">
                        <div class="flex-grow-1 pr-2">
                          <div class="text-body-2">
                            <span class="portal-grupo-familiar__nombre-familiar text-uppercase font-weight-bold">{{
                              nombreApellidoFamiliar(p)
                            }}</span>
                            <span
                              v-if="p.parentescoNombre"
                              class="text-caption portal-grupo-familiar__parentesco-inline"
                            >
                              ({{ p.parentescoNombre }})
                            </span>
                          </div>
                          <div class="text-caption portal-grupo-familiar__linea-detalle">
                            {{ p.tipoDoc }}: {{ p.nroDoc }}
                          </div>
                          <div class="text-caption portal-grupo-familiar__linea-detalle">
                            Fecha de nacimiento: {{ p.fechaNacimiento }}
                          </div>
                        </div>
                        <div class="d-flex flex-shrink-0 align-start">
                          <v-btn
                            :icon="deleteIcon"
                            variant="text"
                            color="error"
                            size="small"
                            density="comfortable"
                            :aria-label="'Quitar vínculo ' + nombreApellidoFamiliar(p)"
                            @click="pedirEliminar(p)"
                          />
                        </div>
                      </div>
                      <div class="portal-grupo-familiar__familiar-card-chip">
                        <v-chip
                          v-if="p.confirmado"
                          size="small"
                          color="success"
                          variant="tonal"
                          class="portal-grupo-familiar__chip-estado font-weight-bold"
                        >
                          Confirmado
                        </v-chip>
                        <v-chip
                          v-else
                          size="small"
                          color="warning"
                          variant="tonal"
                          class="portal-grupo-familiar__chip-estado font-weight-bold"
                        >
                          Pendiente
                        </v-chip>
                      </div>
                    </div>
                  </v-card>
                </div>
              </div>
            </template>
          </v-col>

          <v-col
            v-if="graficoColumnaVisible"
            cols="12"
            md="7"
            class="portal-grupo-familiar__graph-col d-flex flex-column"
          >
            <div
              ref="grafoWrap"
              class="portal-grupo-familiar__cy-wrap elevation-1 pa-3 flex-grow-1 d-flex align-center justify-center"
            >
              <svg
                class="portal-grupo-familiar__grafo-svg"
                :viewBox="grafo.viewBox || '0 0 440 440'"
                preserveAspectRatio="xMidYMid meet"
                :style="{ minWidth: grafo.renderWidth ? `${grafo.renderWidth}px` : null }"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <g :transform="grafo.rootTransform || 'translate(0,0)'">
                <g v-for="(seg, idx) in grafoLineas" :key="'ln-' + idx">
                  <line
                    :x1="seg.x1"
                    :y1="seg.y1"
                    :x2="seg.x2"
                    :y2="seg.y2"
                    :stroke="grafoLineColor"
                    stroke-width="2"
                    stroke-dasharray="6 6"
                  />
                </g>
                <g v-for="n in grafo.familiares" :key="'f-' + n.key">
                  <circle
                    :cx="n.x"
                    :cy="n.y"
                    :r="grafo.nodeRadius || 36"
                    :fill="grafoNodeSurfaceColor"
                    :stroke="grafo.colorPrimary"
                    stroke-width="2"
                  />
                  <text
                    :x="n.x"
                    :y="n.y"
                    text-anchor="middle"
                    dominant-baseline="middle"
                    :fill="grafo.colorPrimary"
                    :font-size="grafo.initialFontSize || 18"
                    font-weight="bold"
                  >
                    {{ n.ini }}
                  </text>
                  <foreignObject
                    :x="n.x - (grafo.labelWidth || 124) / 2"
                    :y="n.y + (grafo.labelY || 45)"
                    :width="grafo.labelWidth || 124"
                    :height="grafo.labelHeight || 42"
                  >
                    <div xmlns="http://www.w3.org/1999/xhtml" class="portal-grupo-familiar__grafo-label">
                      <div
                        class="portal-grupo-familiar__grafo-label-nombre"
                        :style="{ fontSize: `${grafo.labelFontSize || 12}px` }"
                      >
                        {{ n.nombreCortoUpper }}
                      </div>
                      <div
                        v-if="n.rel"
                        class="portal-grupo-familiar__grafo-label-rel"
                        :style="{ color: grafo.colorPrimary, fontSize: `${grafo.relFontSize || 10}px` }"
                      >
                        {{ n.rel }}
                      </div>
                    </div>
                  </foreignObject>
                </g>
                <circle
                  :cx="grafo.pacienteCx"
                  :cy="grafo.pacienteCy"
                  :r="grafo.patientRadius || 38"
                  :fill="grafo.colorPrimary"
                />
                <text
                  :x="grafo.pacienteCx"
                  :y="grafo.pacienteCy"
                  text-anchor="middle"
                  dominant-baseline="middle"
                  :fill="grafoPacienteTextColor"
                  :font-size="grafo.initialFontSize || 18"
                  font-weight="bold"
                >
                  {{ grafo.iniPaciente }}
                </text>
                <foreignObject
                  :x="grafo.pacienteCx - (grafo.patientLabelWidth || 140) / 2"
                  :y="grafo.pacienteCy + (grafo.patientLabelY || 48)"
                  :width="grafo.patientLabelWidth || 140"
                  :height="grafo.patientLabelHeight || 48"
                >
                  <div xmlns="http://www.w3.org/1999/xhtml" class="portal-grupo-familiar__grafo-label portal-grupo-familiar__grafo-label-paciente">
                    <div
                      class="portal-grupo-familiar__grafo-label-nombre"
                      :style="{ fontSize: `${grafo.patientLabelFontSize || 13}px` }"
                    >
                      {{ grafo.nombrePacienteCortoUpper }}
                    </div>
                    <div
                      class="portal-grupo-familiar__grafo-label-rel"
                      :style="{ color: grafo.colorPrimary, fontSize: `${grafo.relFontSize || 10}px` }"
                    >
                      (Yo)
                    </div>
                  </div>
                </foreignObject>
                </g>
              </svg>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialogAgregar" max-width="650" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-h6 font-weight-bold text-primary mt-2">
          Solicitar nuevo vínculo familiar
        </v-card-title>
        <v-card-text class="py-0">
          <v-form ref="formAgregarRef" v-model="formAgregarValid" @submit.prevent="guardarAgregar">
            <v-row dense>
              <v-col cols="12">
                <TipoDocumentoTextField
                  v-model:tipo-id="formAgregar.tdId"
                  v-model:numero="formAgregar.nroDoc"
                  :rules-nro-doc="[rules.required]"
                />
              </v-col>
              <v-col cols="12" md="6">
                <DatePicker
                  v-model="formAgregar.fechaNacimiento"
                  label="Fecha de nacimiento"
                  :min-date="minFechaNacimiento"
                  :max-date="maxFechaNacimiento"
                  :custom-rules="fechaNacimientoFamiliarRules"
                  force-menu-below
                  required
                  hide-details="auto"
                  clearable
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="formAgregar.parentescoOrigenADestinoId"
                  :items="parentescoItems"
                  item-title="value"
                  item-value="id"
                  label="Parentesco respecto a usted"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-4 py-3">
          <v-spacer />
          <v-btn variant="outlined" @click="cerrarAgregar">Cancelar</v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :loading="guardando"
            :disabled="guardando || formAgregarValid === false"
            @click="guardarAgregar"
          >
            Solicitar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog
      v-model="dialogEliminar"
      title="Quitar vínculo familiar"
      :message="mensajeEliminarVinculo"
      confirm-text="Quitar"
      cancel-text="No"
      confirm-color="error"
      :loading="eliminando"
      @confirm="confirmarEliminar"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import enums from '../../../utils/enums'
import PortalPageHeader from '../../../components/layout/PortalPageHeader.vue'
import rules from '../../../utils/helpers/rules'
import {
  addDaysToIsoLocal,
  normalizeIsoDateString,
  todayIsoLocal,
} from '../../../utils/helpers/portalDateFormat'
import TipoDocumentoTextField from '../../../components/shared/TipoDocumentoTextField.vue'
import DatePicker from '../../../components/shared/DatePicker.vue'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog.vue'
import portalScreenSubtitleMixin from '../../../mixins/portalScreenSubtitle'

function formatNombreApellidoPaciente(paciente) {
  const nombre = String(paciente?.nombre ?? '').trim()
  const apellido = String(paciente?.apellido ?? '').trim()
  const nombreApellido = `${nombre} ${apellido}`.trim()
  return nombreApellido || String(paciente?.nombreCompleto ?? '').trim()
}

/** Alineado con Geclisa `FichaAdvertenciasService.NormalizarParentescoNombre` / `ClasificarGrupoParentescoVisual`. */
function normalizarParentescoNombre(nombre) {
  if (!nombre || !String(nombre).trim()) return ''
  return String(nombre)
    .trim()
    .toUpperCase()
    .replace(/Á/g, 'A')
    .replace(/É/g, 'E')
    .replace(/Í/g, 'I')
    .replace(/Ó/g, 'O')
    .replace(/Ú/g, 'U')
}

function clasificarGrupoParentescoVisual(nombre) {
  const n = normalizarParentescoNombre(nombre)
  if (n.includes('ABUELO') || n.includes('ABUELA') || n.includes('ABUEL')) {
    return 'ABUELOS'
  }
  if (
    n.includes('CONYUG') ||
    n.includes('ESPOS') ||
    n.includes('PAREJA') ||
    n.includes('COMPANERO') ||
    n.includes('COMPAÑERO')
  ) {
    return 'CÓNYUGE'
  }
  if (n.includes('HERMAN')) return 'HERMANOS'
  if (n.includes('NIETO') || n.includes('NIETA') || n.includes('NIET')) return 'NIETOS'
  if (n.includes('HIJO') || n.includes('HIJA') || (n.includes('PERSONA') && n.includes('CARGO'))) {
    return 'HIJOS'
  }
  if (
    n.includes('TUTOR') ||
    ((n.includes('PADRE') || n.includes('MADRE') || n.includes('MADRAST')) &&
      !n.includes('ABUELO') &&
      !n.includes('ABUELA'))
  ) {
    return 'PADRES'
  }
  return 'OTROS'
}

export default {
  name: 'PortalGrupoFamiliar',
  components: { TipoDocumentoTextField, DatePicker, ConfirmDialog, PortalPageHeader },
  mixins: [portalScreenSubtitleMixin],
  data() {
    return {
      infoAlertIcon: enums.icons.SNB_INFO,
      emptyFamilyIcon: enums.icons.ACCOUNT_MULTIPLE,
      deleteIcon: enums.icons.DELETE,
      addMemberIcon: enums.icons.ACCOUNT_PLUS_OUTLINE,
      rules,
      loading: true,
      profileNombre: '',
      profileApellido: '',
      pacienteFechaNacimientoIso: '',
      vinculos: [],
      parentescosRaw: [],
      dialogAgregar: false,
      dialogEliminar: false,
      eliminarTarget: null,
      guardando: false,
      eliminando: false,
      formAgregar: {
        tdId: null,
        nroDoc: '',
        fechaNacimiento: '',
        parentescoOrigenADestinoId: null,
      },
      formAgregarValid: null,
      /** Pestañas móvil: 0 = Integrantes, 1 = Gráfico (en md+ se muestran ambas columnas). */
      mobileGrupoTab: 0,
    }
  },
  computed: {
    ...mapState('portalHome', ['activePortalFichaId']),
    mensajeEliminarVinculo() {
      const n = this.nombreApellidoFamiliar(this.eliminarTarget)
      return n
        ? `¿Confirma que desea eliminar el vínculo familiar con «${n}»?`
        : '¿Confirma que desea eliminar este vínculo familiar?'
    },
    /** Misma regla que el grid `md` de Vuetify (v-col md=*). */
    portalMdAndUp() {
      return this.$vuetify.display.mdAndUp
    },
    listaColumnaVisible() {
      return this.portalMdAndUp || Number(this.mobileGrupoTab) === 0
    },
    graficoColumnaVisible() {
      return this.portalMdAndUp || Number(this.mobileGrupoTab) === 1
    },
    /**
     * El `DatePicker` ya limita por defecto a hoy. Para padres y abuelos, además
     * acotamos al día anterior a la fecha de nacimiento del paciente.
     */
    maxFechaNacimiento() {
      const hoy = todayIsoLocal()
      if (
        (this.grupoParentescoSeleccionado === 'PADRES' ||
          this.grupoParentescoSeleccionado === 'ABUELOS') &&
        this.pacienteFechaNacimientoIso
      ) {
        const tope = addDaysToIsoLocal(this.pacienteFechaNacimientoIso, -1)
        if (tope && tope < hoy) return tope
      }
      return hoy
    },
    /** Si el parentesco es hijo/a o nieto/a se exige que nazca al menos un día después del paciente. */
    minFechaNacimiento() {
      if (
        (this.grupoParentescoSeleccionado === 'HIJOS' ||
          this.grupoParentescoSeleccionado === 'NIETOS') &&
        this.pacienteFechaNacimientoIso
      ) {
        return addDaysToIsoLocal(this.pacienteFechaNacimientoIso, 1) || ''
      }
      return ''
    },
    /**
     * Reglas custom para el `DatePicker` del familiar. Se reevalúa al cambiar el
     * parentesco para mostrar un mensaje específico antes de enviar el payload.
     */
    fechaNacimientoFamiliarRules() {
      const grupo = this.grupoParentescoSeleccionado
      const pacIso = normalizeIsoDateString(this.pacienteFechaNacimientoIso)
      if (!pacIso || (grupo !== 'PADRES' && grupo !== 'ABUELOS' && grupo !== 'HIJOS' && grupo !== 'NIETOS')) return []
      if (grupo === 'PADRES' || grupo === 'ABUELOS') {
        return [
          (v) => {
            const s = String(v ?? '').trim()
            if (s === '') return true
            const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s)
            if (!m) return true
            const iso = `${m[3]}-${String(m[2]).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`
            return (
              iso < pacIso ||
              'La fecha de nacimiento no es compatible con el parentesco seleccionado.'
            )
          },
        ]
      }
      return [
        (v) => {
          const s = String(v ?? '').trim()
          if (s === '') return true
          const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s)
          if (!m) return true
          const iso = `${m[3]}-${String(m[2]).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`
          return (
            iso > pacIso ||
            'La fecha de nacimiento no es compatible con el parentesco seleccionado.'
          )
        },
      ]
    },
    parentescoItems() {
      return this.completarParentescosInversosLocales(this.parentescosRaw).filter(
        (p) => p.parentescoInversoId != null && p.parentescoInversoId > 0,
      )
    },
    parentescoSeleccionadoNombre() {
      const id = this.formAgregar.parentescoOrigenADestinoId
      if (id == null) return ''
      const it = (this.parentescosRaw || []).find((p) => p.id === id)
      return it ? String(it.value || '') : ''
    },
    grupoParentescoSeleccionado() {
      return clasificarGrupoParentescoVisual(this.parentescoSeleccionadoNombre)
    },
    /** Misma agrupación que `GrupoFamiliarDialog.vue` (geclisa-site). */
    gruposOrdenados() {
      const orden = ['ABUELOS', 'PADRES', 'CÓNYUGE', 'HERMANOS', 'HIJOS', 'NIETOS', 'OTROS']
      const titulos = {
        ABUELOS: 'ABUELOS',
        PADRES: 'PADRES',
        HERMANOS: 'HERMANOS',
        HIJOS: 'HIJOS',
        NIETOS: 'NIETOS',
        'CÓNYUGE': 'CÓNYUGE',
        OTROS: 'OTROS',
      }
      const map = {}
      for (const p of this.vinculos || []) {
        const g = p.grupo || 'OTROS'
        if (!map[g]) map[g] = []
        map[g].push(p)
      }
      return orden
        .filter((k) => map[k] && map[k].length)
        .map((k) => ({ key: k, titulo: titulos[k] || k, items: map[k] }))
    },
    pacienteNombreCompleto() {
      const a = String(this.profileApellido || '').trim()
      const n = String(this.profileNombre || '').trim()
      const full = `${a} ${n}`.trim()
      return full || 'Paciente'
    },
    grafo() {
      return this.buildGrafoArbol()
    },
    grafoLineas() {
      const g = this.grafo
      return (g.familiares || [])
        .filter((n) => n.conectar !== false)
        .map((n) => ({
          x1: g.pacienteCx,
          y1: g.pacienteCy,
          x2: n.x,
          y2: n.y,
        }))
    },
    portalDarkModeActivo() {
      const themeDark = this.$vuetify?.theme?.global?.current?.value?.dark
      if (themeDark != null) return !!themeDark
      if (typeof document === 'undefined') return false
      return document.documentElement.classList.contains('portal-dark-mode')
    },
    grafoNodeSurfaceColor() {
      return this.portalDarkModeActivo ? '#111820' : '#ffffff'
    },
    grafoTextColor() {
      return this.portalDarkModeActivo ? '#E8EEF5' : '#424242'
    },
    grafoPacienteTextColor() {
      return '#ffffff'
    },
    grafoLineColor() {
      return this.portalDarkModeActivo ? 'rgba(144, 202, 249, 0.45)' : '#B0BEC5'
    },
  },
  watch: {
    activePortalFichaId() {
      this.dialogAgregar = false
      this.dialogEliminar = false
      this.eliminarTarget = null
      this.cargarTodo()
    },
    'formAgregar.parentescoOrigenADestinoId'() {
      if (!this.dialogAgregar) return
      this.$nextTick(() => {
        const r = this.$refs.formAgregarRef
        if (r && typeof r.validate === 'function' && this.formAgregar.fechaNacimiento) {
          r.validate()
        }
      })
    },
  },
  mounted() {
    this.cargarTodo()
  },
  methods: {
    ...mapActions({
      setAlert: 'alerts/setAlert',
      fetchVinculos: 'grupoFamiliar/fetchVinculos',
      fetchParentescosParaFicha: 'grupoFamiliar/fetchParentescosParaFicha',
      agregarVinculo: 'grupoFamiliar/agregarVinculo',
      eliminarVinculo: 'grupoFamiliar/eliminarVinculo',
      fetchHomeProfile: 'portalHome/fetchHomeProfile',
      fetchMisDatos: 'misDatos/fetchMisDatos',
    }),
    nombreApellidoFamiliar(p) {
      return formatNombreApellidoPaciente(p)
    },
    /** Misma lógica que `GrupoFamiliarDialog.vue` en geclisa-site. */
    iniciales(nombreCompleto) {
      if (!nombreCompleto) return '?'
      const partes = String(nombreCompleto).trim().split(/\s+/).filter(Boolean)
      if (!partes.length) return '?'
      if (partes.length === 1) return partes[0].substring(0, 2).toUpperCase()
      return (partes[0][0] + partes[1][0]).toUpperCase()
    },
    filasTabla(items) {
      return (items || []).map((v) => ({
        ...v,
        doc: `${v.tipoDoc} ${v.nroDoc}`.trim(),
        grupo: clasificarGrupoParentescoVisual(v.parentescoNombre),
      }))
    },
    spreadRowXs(count, centerX, spanTotal) {
      if (count <= 0) return []
      if (count === 1) return [Math.round(centerX)]
      const left = centerX - spanTotal / 2
      const step = spanTotal / (count - 1)
      return Array.from({ length: count }, (_, i) => Math.round(left + i * step))
    },
    normGrupoArbol(p) {
      const raw0 = ((p && p.grupo) || '').toString().trim().toUpperCase()
      const raw = raw0.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      if (raw.includes('ABUELO') || raw.includes('ABUELA') || raw.includes('ABUEL')) return 'ABUELOS'
      if (raw.includes('TUTOR') || raw.includes('PADRE') || raw === 'PADRES') return 'PADRES'
      if (raw.includes('HERMAN')) return 'HERMANOS'
      if (raw.includes('NIETO') || raw.includes('NIETA') || raw.includes('NIET')) return 'NIETOS'
      if (
        raw.includes('HIJOS') ||
        raw.includes('HIJO') ||
        raw.includes('HIJA') ||
        (raw.includes('PERSONA') && raw.includes('CARGO'))
      ) {
        return 'HIJOS'
      }
      if (raw.includes('CONYUGE') || raw.includes('PAREJA')) return 'CONYUGE'
      if (raw.includes('OTRO')) return 'OTROS'
      return raw || 'OTROS'
    },
    mapNodoFamiliar(p, x, y, grupo = 'OTROS', conectar = true) {
      const nom = this.nombreApellidoFamiliar(p)
      const maxNom = 42
      const nombreCorto = nom.length > maxNom ? `${nom.slice(0, maxNom - 1)}…` : nom
      const rawRel = (p.parentescoNombre || '').trim()
      const rel = rawRel ? `(${rawRel})` : ''
      const fpId =
        p.fichaParentescoId != null ? p.fichaParentescoId : `tmp-${x}-${y}-${rel}`
      return {
        key: fpId,
        x: Math.round(x),
        y: Math.round(y),
        ini: this.iniciales(nom),
        nombreCorto,
        nombreCortoUpper: nombreCorto.toUpperCase(),
        rel,
        grupo,
        conectar,
      }
    },
    buildGrafoArbol() {
      let colorPrimary = '#00897B'
      try {
        if (typeof document !== 'undefined' && this.$el) {
          const v = getComputedStyle(this.$el).getPropertyValue('--portal-primary').trim()
          if (v) colorPrimary = v
        }
      } catch {
        /* fallback */
      }
      const pacienteNombre = this.pacienteNombreCompleto
      const lista = this.vinculos || []
      const abuelos = []
      const padres = []
      const conyuges = []
      const hermanos = []
      const hijos = []
      const nietos = []
      const otros = []
      for (const p of lista) {
        const c = this.normGrupoArbol(p)
        if (c === 'ABUELOS') abuelos.push(p)
        else if (c === 'PADRES') padres.push(p)
        else if (c === 'CONYUGE') conyuges.push(p)
        else if (c === 'HERMANOS') hermanos.push(p)
        else if (c === 'HIJOS') hijos.push(p)
        else if (c === 'NIETOS') nietos.push(p)
        else otros.push(p)
      }
      const hasConyuge = conyuges.length > 0
      const hasHermanos = hermanos.length > 0
      const lateralMax = Math.max(conyuges.length, hermanos.length)
      const maxFila = Math.max(abuelos.length, padres.length, hijos.length, nietos.length, lateralMax, otros.length)
      const compacto = lista.length >= 8 || maxFila > 3
      const nodeRadius = compacto ? 28 : 36
      const patientRadius = compacto ? 30 : 38
      const initialFontSize = compacto ? 15 : 18
      const labelWidth = compacto ? 104 : 124
      const labelHeight = compacto ? 36 : 42
      const labelY = compacto ? 36 : 45
      const labelFontSize = compacto ? 10 : 12
      const relFontSize = compacto ? 9 : 10
      const patientLabelWidth = compacto ? 116 : 140
      const patientLabelHeight = compacto ? 42 : 48
      const patientLabelY = compacto ? 38 : 48
      const patientLabelFontSize = compacto ? 11 : 13
      const svgW = lateralMax > 1 ? 680 + (lateralMax - 2) * 120 : hasConyuge || hasHermanos ? 540 : 440
      const hasAbuelos = abuelos.length > 0
      const hasNietos = nietos.length > 0
      const svgH = 440 + (hasAbuelos ? 120 : 0) + (hasNietos ? 120 : 0)
      const centerX = hasConyuge ? 200 : 220
      const diagOffset = 95
      const grandparentY = 58
      const parentY = hasAbuelos ? 190 : 72
      const midY = hasAbuelos ? 335 : 218
      const childY = hasAbuelos ? 475 : 368
      const grandchildY = childY + 110
      const pacienteX = centerX
      const pacienteY = midY
      const familiares = []

      if (abuelos.length === 2) {
        familiares.push(this.mapNodoFamiliar(abuelos[0], centerX - diagOffset, grandparentY, 'ABUELOS'))
        familiares.push(this.mapNodoFamiliar(abuelos[1], centerX + diagOffset, grandparentY, 'ABUELOS'))
      } else if (abuelos.length === 1) {
        familiares.push(this.mapNodoFamiliar(abuelos[0], centerX - diagOffset, grandparentY, 'ABUELOS'))
      } else if (abuelos.length > 2) {
        const spanAbuelos = Math.max(compacto ? 190 : 220, (abuelos.length - 1) * (compacto ? 112 : 132))
        const xsAbuelos = this.spreadRowXs(abuelos.length, centerX, spanAbuelos)
        abuelos.forEach((a, i) => {
          familiares.push(this.mapNodoFamiliar(a, xsAbuelos[i], grandparentY, 'ABUELOS'))
        })
      }

      if (padres.length === 2) {
        familiares.push(this.mapNodoFamiliar(padres[0], centerX - diagOffset, parentY, 'PADRES'))
        familiares.push(this.mapNodoFamiliar(padres[1], centerX + diagOffset, parentY, 'PADRES'))
      } else if (padres.length === 1) {
        familiares.push(this.mapNodoFamiliar(padres[0], centerX, parentY, 'PADRES'))
      } else if (padres.length > 2) {
        const spanPadres = Math.max(compacto ? 190 : 220, (padres.length - 1) * (compacto ? 112 : 132))
        const xsPadres = this.spreadRowXs(padres.length, centerX, spanPadres)
        padres.forEach((pr, i) => {
          familiares.push(this.mapNodoFamiliar(pr, xsPadres[i], parentY, 'PADRES'))
        })
      }

      const usaSeparacionExtendida = lateralMax > 1
      const offsetParejaDesdeCentro = compacto ? 152 : usaSeparacionExtendida ? 185 : 175
      const pasoPareja = compacto ? 108 : usaSeparacionExtendida ? 124 : 102
      const offsetHermanoDesdeCentro = compacto ? 152 : usaSeparacionExtendida ? 185 : 175
      const pasoHermano = compacto ? 108 : usaSeparacionExtendida ? 124 : 102
      hermanos.forEach((p, i) => {
        const x = centerX - offsetHermanoDesdeCentro - i * pasoHermano
        familiares.push(this.mapNodoFamiliar(p, x, midY, 'HERMANOS'))
      })

      conyuges.forEach((p, i) => {
        const x = centerX + offsetParejaDesdeCentro + i * pasoPareja
        familiares.push(this.mapNodoFamiliar(p, x, midY, 'CONYUGE'))
      })

      if (hijos.length === 2) {
        familiares.push(this.mapNodoFamiliar(hijos[0], centerX - diagOffset, childY, 'HIJOS'))
        familiares.push(this.mapNodoFamiliar(hijos[1], centerX + diagOffset, childY, 'HIJOS'))
      } else if (hijos.length === 1) {
        familiares.push(this.mapNodoFamiliar(hijos[0], centerX, childY, 'HIJOS'))
      } else if (hijos.length > 2) {
        const spanHijos = Math.max(compacto ? 160 : 180, (hijos.length - 1) * (compacto ? 108 : 124))
        const xsHijos = this.spreadRowXs(hijos.length, centerX, spanHijos)
        hijos.forEach((h, i) => {
          familiares.push(this.mapNodoFamiliar(h, xsHijos[i], childY, 'HIJOS'))
        })
      }

      if (nietos.length === 2) {
        familiares.push(this.mapNodoFamiliar(nietos[0], centerX - diagOffset, grandchildY, 'NIETOS'))
        familiares.push(this.mapNodoFamiliar(nietos[1], centerX + diagOffset, grandchildY, 'NIETOS'))
      } else if (nietos.length === 1) {
        familiares.push(this.mapNodoFamiliar(nietos[0], centerX + diagOffset, grandchildY, 'NIETOS'))
      } else if (nietos.length > 2) {
        const spanNietos = Math.max(compacto ? 160 : 180, (nietos.length - 1) * (compacto ? 108 : 124))
        const xsNietos = this.spreadRowXs(nietos.length, centerX, spanNietos)
        nietos.forEach((n, i) => {
          familiares.push(this.mapNodoFamiliar(n, xsNietos[i], grandchildY, 'NIETOS'))
        })
      }

      otros.forEach((p, i) => {
        const ox = centerX + 182
        const oy = midY + (i - (otros.length - 1) / 2) * 88
        familiares.push(this.mapNodoFamiliar(p, ox, Math.min(svgH - 40, Math.max(80, oy)), 'OTROS'))
      })

      const maxPacNom = 36
      const nombrePacienteCorto =
        pacienteNombre.length > maxPacNom
          ? `${pacienteNombre.slice(0, maxPacNom - 2)}…`
          : pacienteNombre
      const nombrePacienteCortoUpper = nombrePacienteCorto.toUpperCase()

      const labelHalfWidth = Math.max(labelWidth, patientLabelWidth) / 2
      const paddingX = 32
      const xs = [pacienteX, ...familiares.map((n) => n.x)]
      const minX = Math.min(...xs) - labelHalfWidth
      const maxX = Math.max(...xs) + labelHalfWidth
      const boundsW = maxX - minX + paddingX * 2
      const finalSvgW = Math.max(svgW, boundsW)
      const rootTx = Math.round(paddingX - minX + (finalSvgW - boundsW) / 2)

      return {
        viewBox: `0 0 ${finalSvgW} ${svgH}`,
        renderWidth: Math.min(compacto ? 760 : 920, Math.max(440, finalSvgW)),
        rootTransform: `translate(${rootTx}, 0)`,
        pacienteCx: pacienteX,
        pacienteCy: pacienteY,
        nodeRadius,
        patientRadius,
        initialFontSize,
        labelWidth,
        labelHeight,
        labelY,
        labelFontSize,
        relFontSize,
        patientLabelWidth,
        patientLabelHeight,
        patientLabelY,
        patientLabelFontSize,
        colorPrimary,
        iniPaciente: this.iniciales(pacienteNombre),
        nombrePacienteCorto,
        nombrePacienteCortoUpper,
        familiares,
      }
    },
    async cargarTodo() {
      this.loading = true
      try {
        const [v, p, mis] = await Promise.all([
          this.fetchVinculos(),
          this.fetchParentescosParaFicha(),
          this.fetchMisDatos(),
          this.fetchHomeProfile({ fichaId: null }),
        ])
        const home = this.$store.state.portalHome || {}
        this.profileNombre = String(home.profileNombre ?? '').trim()
        this.profileApellido = String(home.profileApellido ?? '').trim()
        this.pacienteFechaNacimientoIso = normalizeIsoDateString(
          mis?.ficha?.fechaNacimiento ?? '',
        )
        this.vinculos = this.filasTabla(v)
        this.parentescosRaw = Array.isArray(p) ? p : []
      } catch {
        this.pacienteFechaNacimientoIso = ''
        this.vinculos = []
        this.parentescosRaw = []
      } finally {
        this.loading = false
      }
    },
    abrirAgregar() {
      this.formAgregar = {
        tdId: null,
        nroDoc: '',
        fechaNacimiento: '',
        parentescoOrigenADestinoId: null,
      }
      this.dialogAgregar = true
      this.$nextTick(() => this.$refs.formAgregarRef?.resetValidation?.())
    },
    cerrarAgregar() {
      this.dialogAgregar = false
    },
    completarParentescosInversosLocales(parentescos) {
      const items = Array.isArray(parentescos) ? parentescos : []
      const buscarId = (...tokens) => {
        const hit = items.find((p) => {
          const nombre = normalizarParentescoNombre(p?.value)
          return tokens.every((t) => nombre.includes(t))
        })
        return hit?.id > 0 ? hit.id : null
      }

      return items.map((p) => {
        if (!p || p.parentescoInversoId > 0) return p
        const nombre = normalizarParentescoNombre(p.value)
        let parentescoInversoId = null

        if (nombre.includes('TUTOR')) {
          parentescoInversoId = buscarId('PERSONA', 'CARGO')
        } else if (nombre.includes('PERSONA') && nombre.includes('CARGO')) {
          parentescoInversoId = buscarId('TUTOR')
        } else if (nombre.includes('ABUELO') || nombre.includes('ABUELA') || nombre.includes('ABUEL')) {
          parentescoInversoId = buscarId('NIET')
        } else if (nombre.includes('NIETO') || nombre.includes('NIETA') || nombre.includes('NIET')) {
          parentescoInversoId = buscarId('ABUEL')
        } else if (nombre.includes('HERMAN')) {
          parentescoInversoId = buscarId('HERMAN')
        }

        return parentescoInversoId > 0 ? { ...p, parentescoInversoId } : p
      })
    },
    obtenerLimiteVinculo(nombre) {
      const n = normalizarParentescoNombre(nombre)
      if (n === 'PADRE' || n === 'MADRE' || n.includes('MADRAST')) {
        return { tipo: 'PARENTAL', maximo: 2, nombre: 'parental' }
      }
      if (n.includes('TUTOR')) {
        return { tipo: 'TUTOR', maximo: 1, nombre: 'Tutor' }
      }
      if (n.includes('ABUELO') || n.includes('ABUELA') || n.includes('ABUEL')) {
        return { tipo: 'ABUELOS', maximo: 4, nombre: 'abuelos' }
      }
      if (
        n.includes('CONYUG') ||
        n.includes('ESPOS') ||
        n.includes('PAREJA') ||
        n.includes('COMPANERO') ||
        n.includes('COMPAN')
      ) {
        return { tipo: 'CONYUGE', maximo: 1, nombre: 'Cónyuge' }
      }
      return null
    },
    mensajeLimiteVinculo(limite) {
      if (limite.maximo === 1) {
        return `Ya existe un vínculo de tipo ${limite.nombre} en el grupo familiar. Solo puede registrar uno por persona.`
      }
      return `Ya alcanzó el máximo de ${limite.maximo} vínculos de tipo ${limite.nombre} en el grupo familiar.`
    },
    validarLimiteVinculo(nombreRelacion) {
      const limite = this.obtenerLimiteVinculo(nombreRelacion)
      if (!limite) return true
      const cantidad = (this.vinculos || []).filter((p) => {
        const actual = this.obtenerLimiteVinculo(p.parentescoNombre)
        return actual && actual.tipo === limite.tipo
      }).length
      if (cantidad < limite.maximo) return true
      this.setAlert({
        type: 'warning',
        message: this.mensajeLimiteVinculo(limite),
      })
      return false
    },
    async guardarAgregar() {
      const { valid } = await this.$refs.formAgregarRef.validate()
      if (!valid) return
      const td = this.formAgregar.tdId
      if (td == null || !Number.isFinite(Number(td))) {
        this.setAlert({
          type: 'warning',
          message: 'Seleccione el tipo de documento.',
        })
        return
      }
      const pid = this.formAgregar.parentescoOrigenADestinoId
      if (pid == null || !Number.isFinite(Number(pid))) {
        this.setAlert({
          type: 'warning',
          message: 'Seleccione el parentesco.',
        })
        return
      }
      if (!this.validarLimiteVinculo(this.parentescoSeleccionadoNombre)) return
      this.guardando = true
      try {
        await this.agregarVinculo({
          tdId: Number(td),
          nroDoc: String(this.formAgregar.nroDoc || '').trim(),
          fechaNacimiento: String(this.formAgregar.fechaNacimiento || '').trim(),
          parentescoOrigenADestinoId: Number(this.formAgregar.parentescoOrigenADestinoId),
        })
        this.setAlert({
          type: 'success',
          message: 'Solicitud registrada. El vínculo figura como pendiente hasta que la institución lo confirme.',
        })
        this.dialogAgregar = false
        await this.cargarTodo()
      } catch {
        /* axios interceptor ya muestra el mensaje del servidor */
      } finally {
        this.guardando = false
      }
    },
    pedirEliminar(item) {
      this.eliminarTarget = item
      this.dialogEliminar = true
    },
    async confirmarEliminar() {
      const id = this.eliminarTarget?.fichaParentescoId
      if (id == null || !Number.isFinite(Number(id))) {
        this.dialogEliminar = false
        return
      }
      this.eliminando = true
      try {
        await this.eliminarVinculo(Number(id))
        this.setAlert({
          type: 'success',
          message: 'El vínculo fue eliminado.',
        })
        this.dialogEliminar = false
        this.eliminarTarget = null
        await this.cargarTodo()
      } catch {
        /* mensaje vía interceptor */
      } finally {
        this.eliminando = false
      }
    },
  },
}
</script>

<style scoped>
.portal-grupo-familiar__lista-col {
  min-height: 320px;
}
.portal-grupo-familiar__graph-col {
  min-height: 320px;
}
.portal-grupo-familiar__empty {
  min-height: 280px;
  padding-top: 1rem;
  padding-bottom: 1.5rem;
}
.portal-grupo-familiar__lista-scroll {
  max-height: 520px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.28) transparent;
}
.portal-grupo-familiar__lista-scroll::-webkit-scrollbar {
  width: 6px;
}
.portal-grupo-familiar__lista-scroll::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.22);
  border-radius: 6px;
}
.portal-grupo-familiar__lista-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.portal-grupo-familiar__cy-wrap {
  width: 100%;
  min-height: 320px;
  background: rgb(var(--v-theme-background));
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: hidden;
}
.portal-grupo-familiar--dark .portal-grupo-familiar__cy-wrap {
  background: #111820 !important;
}
.portal-grupo-familiar__grafo-svg {
  width: 100%;
  max-width: none;
  height: auto;
  max-height: 680px;
}
.portal-grupo-familiar__grafo-label {
  color: v-bind(grafoTextColor);
  font-size: 12px;
  line-height: 1.12;
  text-align: center;
  overflow: hidden;
}
.portal-grupo-familiar__grafo-label-nombre {
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.portal-grupo-familiar__grafo-label-rel {
  font-size: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.portal-grupo-familiar__grafo-label-paciente {
  font-size: 13px;
}
.portal-grupo-familiar__grupo-header {
  color: #757575;
  letter-spacing: 0.06em;
}
.portal-grupo-familiar__familiar-card {
  border-color: rgba(var(--v-theme-on-surface), 0.12) !important;
  background: rgb(var(--v-theme-surface));
}
.portal-grupo-familiar--dark .portal-grupo-familiar__familiar-card {
  border-color: rgba(255, 255, 255, 0.12) !important;
  background: #111820 !important;
  background-color: #111820 !important;
  color: #e8eef5;
}
.portal-grupo-familiar__familiar-card-main {
  position: relative;
  padding-bottom: 40px;
}
.portal-grupo-familiar__nombre-familiar {
  color: #424242;
}
.portal-grupo-familiar--dark .portal-grupo-familiar__nombre-familiar {
  color: #e8eef5;
}
.portal-grupo-familiar__parentesco-inline {
  color: #616161;
  font-weight: 400;
}
.portal-grupo-familiar--dark .portal-grupo-familiar__parentesco-inline {
  color: rgba(232, 238, 245, 0.74);
}
.portal-grupo-familiar__linea-detalle {
  color: #424242;
  margin-top: 2px;
}
.portal-grupo-familiar--dark .portal-grupo-familiar__linea-detalle {
  color: rgba(232, 238, 245, 0.82);
}
.portal-grupo-familiar__familiar-card-chip {
  position: absolute;
  right: 8px;
  bottom: 8px;
}
.portal-grupo-familiar__chip-estado {
  font-weight: 700 !important;
}
</style>
